import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatXOF, formatDate, formatDateTime, METHOD_LABELS } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import {
  SignOut, Sun, MoonStars, GraduationCap, Receipt, BookOpen, Warning, UserCircle, Bell,
  TrendUp, ClipboardText, CurrencyDollar, Books, FileText, DownloadSimple, Clock,
} from "@phosphor-icons/react";
import Gradebook from "@/pages/Gradebook";

export default function ParentPortal() {
  const { user, school, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [details, setDetails] = useState({ invoices: [], payments: [], grades: [], attendance: [], assignments: [] });
  const [assignFilter, setAssignFilter] = useState("all");

  const load = async () => {
    try {
      const r = await api.get("/parent/dashboard");
      setDash(r.data);
      if (r.data.children.length && !selectedId) setSelectedId(r.data.children[0].student.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selectedId) return;
    Promise.all([
      api.get("/invoices", { params: { student_id: selectedId } }),
      api.get("/payments", { params: { student_id: selectedId } }),
      api.get("/grades", { params: { student_id: selectedId } }),
      api.get("/attendance", { params: { student_id: selectedId } }),
      api.get("/parent/assignments", { params: { child_id: selectedId } }),
    ]).then(([i, p, g, a, ass]) => {
      setDetails({ invoices: i.data, payments: p.data, grades: g.data, attendance: a.data, assignments: ass.data });
    }).catch(e => toast.error(formatApiError(e)));
  }, [selectedId]);

  const doLogout = async () => { await logout(); nav("/", { replace: true }); };
  if (!dash) return <div className="p-10 text-sm text-muted-foreground">Chargement…</div>;
  const selected = dash.children.find(c => c.student.id === selectedId);
  const totalUnpaid = dash.children.reduce((s, c) => s + (c.unpaid_amount || 0), 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border sticky top-0 z-30 bg-card/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold">SH</div>
            <div>
              <div className="font-heading font-bold text-sm md:text-base leading-none">Portail Parent</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{school?.name || "École"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="btn-theme-parent">{theme === "dark" ? <Sun /> : <MoonStars />}</Button>
            <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground border-l border-border pl-3">
              <UserCircle size={20} weight="duotone" />
              <span>{user?.name}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="btn-logout-parent"><SignOut /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Bienvenue</div>
          <h1 className="mt-1 font-heading text-2xl md:text-3xl font-bold tracking-tight">Bonjour {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {dash.children.length} enfant(s) inscrit(s) · {dash.active_period ? `${dash.active_period.name} en cours` : ""}
            {totalUnpaid > 0 && <> · <span className="text-accent font-semibold">{formatXOF(totalUnpaid)} restant à régler</span></>}
          </p>
        </div>

        {/* Children switcher */}
        <div className="mb-6 flex gap-3 overflow-x-auto pb-2">
          {dash.children.map(c => (
            <button key={c.student.id}
              onClick={() => setSelectedId(c.student.id)}
              className={`shrink-0 rounded-xl border p-4 text-left min-w-[240px] transition-all ${selectedId === c.student.id ? "border-primary bg-primary/5 ring-1 ring-primary/40" : "border-border bg-card hover:border-primary/30"}`}
              data-testid={`child-${c.student.id}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                  {c.student.first_name[0]}{c.student.last_name[0]}
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-semibold truncate">{c.student.first_name} {c.student.last_name}</div>
                  <div className="text-[10px] text-muted-foreground">{c.class_group?.name || "?"} · {c.student.matricule}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1 text-[10px]">
                {c.grade_avg != null && <Badge variant="outline">Moy. {c.grade_avg}/20</Badge>}
                {c.attendance_rate_30d != null && <Badge variant="outline">Prés. {c.attendance_rate_30d}%</Badge>}
                {c.upcoming_assignments > 0 && <Badge variant="outline" className="border-accent text-accent">{c.upcoming_assignments} devoirs</Badge>}
                {c.unpaid_amount > 0 && <Badge variant="outline" className="border-accent text-accent">{formatXOF(c.unpaid_amount)}</Badge>}
              </div>
            </button>
          ))}
        </div>

        {selected && (
          <>
            {/* Stats cards for selected child */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <MiniStat icon={TrendUp} label="Moyenne période" v={selected.grade_avg != null ? `${selected.grade_avg}/20` : "—"} tint="primary" testid="parent-avg" />
              <MiniStat icon={ClipboardText} label="Présence 30 j." v={selected.attendance_rate_30d != null ? `${selected.attendance_rate_30d}%` : "—"} tint="secondary" testid="parent-att-rate" />
              <MiniStat icon={Books} label="Devoirs actifs" v={selected.upcoming_assignments} tint="accent" testid="parent-upcoming" />
              <MiniStat icon={CurrencyDollar} label="Restant à payer" v={selected.unpaid_amount > 0 ? formatXOF(selected.unpaid_amount) : "À jour"} tint={selected.unpaid_amount > 0 ? "warning" : "muted"} testid="parent-unpaid" />
            </div>

            <Tabs defaultValue="fees" data-testid="parent-tabs">
              <TabsList>
                <TabsTrigger value="fees" data-testid="parent-tab-fees"><Receipt size={14} className="mr-1" />Frais</TabsTrigger>
                <TabsTrigger value="assignments" data-testid="parent-tab-assignments"><Books size={14} className="mr-1" />Devoirs ({details.assignments.length})</TabsTrigger>
                <TabsTrigger value="grades" data-testid="parent-tab-grades"><BookOpen size={14} className="mr-1" />Notes détaillées</TabsTrigger>
                <TabsTrigger value="attendance" data-testid="parent-tab-attendance"><Warning size={14} className="mr-1" />Absences</TabsTrigger>
                <TabsTrigger value="notifs" data-testid="parent-tab-notifs"><Bell size={14} className="mr-1" />Notifications ({dash.notifications.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="fees" className="mt-4">
                {details.invoices.length === 0 ? (
                  <Card className="p-10 text-center text-sm text-muted-foreground">Aucune facture.</Card>
                ) : (
                  <Card className="overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-2">Libellé</th><th className="px-4 py-2">Émise</th><th className="px-4 py-2">Échéance</th>
                        <th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2 text-right">Payé</th><th className="px-4 py-2 text-right">Solde</th>
                      </tr></thead>
                      <tbody>
                        {details.invoices.map((i, k) => (
                          <tr key={i.id} className={`border-b border-border last:border-0 ${k % 2 ? "bg-muted/20" : ""}`}>
                            <td className="px-4 py-2 font-medium">{i.label}</td>
                            <td className="px-4 py-2 text-xs">{formatDate(i.issued_at)}</td>
                            <td className="px-4 py-2 text-xs">{i.due_date || "—"}</td>
                            <td className="px-4 py-2 text-right font-mono">{formatXOF(i.total)}</td>
                            <td className="px-4 py-2 text-right font-mono text-secondary">{formatXOF(i.paid)}</td>
                            <td className={`px-4 py-2 text-right font-mono font-bold ${i.balance > 0 ? "text-accent" : "text-secondary"}`}>{formatXOF(i.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                )}
                {details.payments.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Derniers paiements</div>
                    <div className="space-y-1.5">
                      {details.payments.slice(0, 5).map(p => (
                        <div key={p.id} className="flex justify-between items-center text-sm bg-card border border-border rounded px-3 py-2">
                          <div>
                            <div className="font-medium">{formatXOF(p.amount)}</div>
                            <div className="text-[10px] text-muted-foreground">{formatDate(p.date)} · {METHOD_LABELS[p.method] || p.method}</div>
                          </div>
                          <Badge variant="outline">{p.receipt_no || "—"}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="grades" className="mt-4">
                <Gradebook studentIdProp={selectedId} />
              </TabsContent>

              <TabsContent value="assignments" className="mt-4">
                <div className="flex gap-2 mb-3 flex-wrap">
                  {["all","current","upcoming","overdue"].map(f => (
                    <Button key={f} variant={assignFilter === f ? "default" : "outline"} size="sm"
                            onClick={() => setAssignFilter(f)} data-testid={`filter-assign-${f}`}>
                      {{ all: "Tous", current: "En cours", upcoming: "À venir", overdue: "En retard" }[f]}
                    </Button>
                  ))}
                </div>
                {details.assignments.filter(a => assignFilter === "all" || a.state === assignFilter).length === 0 ? (
                  <Card className="p-10 text-center text-sm text-muted-foreground">Aucun devoir dans cette catégorie.</Card>
                ) : (
                  <div className="space-y-2">
                    {details.assignments.filter(a => assignFilter === "all" || a.state === assignFilter).map(a => {
                      const stateColor = { upcoming: "border-primary text-primary", current: "border-secondary text-secondary", overdue: "border-destructive text-destructive" }[a.state];
                      return (
                        <Card key={a.id} className="p-4" data-testid={`parent-assignment-${a.id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-heading font-semibold">{a.title}</div>
                                <Badge variant="outline" className={`text-[10px] ${stateColor}`}>{{ upcoming: "À venir", current: "En cours", overdue: "En retard" }[a.state]}</Badge>
                                <Badge variant="outline" className="text-[10px]">{a.kind}</Badge>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">{a.subject_name} · {a.class_name}{a.due_date ? ` · À rendre avant ${new Date(a.due_date).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</div>
                              {a.description && <div className="text-sm mt-1.5 text-muted-foreground line-clamp-2">{a.description}</div>}
                              {(a.attachments || []).length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {a.attachments.map((att, i) => (
                                    <a key={i} href={att.url} target="_blank" rel="noreferrer"
                                       className="inline-flex items-center gap-1 text-[11px] bg-primary/10 text-primary hover:bg-primary/20 rounded px-2 py-1 border border-primary/30"
                                       data-testid={`parent-file-${a.id}-${i}`}>
                                      <DownloadSimple size={11} weight="bold" /> {att.name}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              {a.submission_grade != null ? (
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Note</div>
                                  <div className="font-heading text-2xl font-bold text-secondary tabular-nums">{a.submission_grade}<span className="text-xs text-muted-foreground font-normal">/{a.max_score}</span></div>
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-[10px]">{{ not_submitted: "Non remis", submitted: "Remis", late: "Remis en retard", graded: "Corrigé" }[a.submission_status] || a.submission_status}</Badge>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="attendance" className="mt-4">
                {details.attendance.length === 0 ? (
                  <Card className="p-10 text-center text-sm text-muted-foreground">Aucune absence signalée.</Card>
                ) : (
                  <div className="space-y-1.5">
                    {details.attendance.map(a => (
                      <div key={a.id} className="flex items-center justify-between text-sm bg-card border border-border rounded px-3 py-2">
                        <div>
                          <div className="font-medium capitalize">{a.status}</div>
                          <div className="text-[10px] text-muted-foreground">{formatDate(a.date)}{a.note ? ` · ${a.note}` : ""}</div>
                        </div>
                        <Badge variant={a.status === "absent" ? "destructive" : "outline"}>{a.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="notifs" className="mt-4">
                {dash.notifications.length === 0 ? (
                  <Card className="p-10 text-center text-sm text-muted-foreground">Aucune notification récente.</Card>
                ) : (
                  <div className="space-y-2">
                    {dash.notifications.map(n => (
                      <Card key={n.id} className="p-3">
                        <div className="text-sm font-semibold">{n.subject || "—"}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{formatDateTime(n.created_at)}</div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, v, tint, testid }) {
  const styles = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent",
    warning: "bg-accent/15 text-accent",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className={`w-8 h-8 rounded-md ${styles[tint] || styles.muted} flex items-center justify-center mb-2`}>
        <Icon size={16} weight="duotone" />
      </div>
      <div className="font-heading text-xl md:text-2xl font-bold tabular-nums truncate">{v}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</div>
    </Card>
  );
}
